import { ResponseStatuses } from '@src/utils/enums';
import { GeospatialEndpoints } from './services/geospatial/config';
import { IpacEndpoints } from './services/ipac/config';
import { toast } from 'react-toastify';
import { AddressValidationEndpoints } from './services/address-validation/config';
import { RequireAtLeastOne } from '@src/utils/typescript';
import { AorEndpoints } from './services/aor/config';
import { UserRoleCapabilityEndpoints } from './services/user-role-capabilities/config';
import { DeveloperAccessEndpoints } from './services/developer/config';
import { ProfileEndpoints } from './services/profile/config';

type SuppressProperties = {
  message?: string;
  statusText?: (typeof ResponseStatuses)[keyof typeof ResponseStatuses];
  statusCode?: number;
  statusCodeRange?: [number, number];
  url?: string;
};

type CustomMessage = {
  customMessage?: {
    toast?: string;
    console?: string;
  };
};

type SuppressMessagingItem = RequireAtLeastOne<
  SuppressProperties,
  'message' | 'statusCode' | 'statusCodeRange' | 'statusText' | 'url'
> &
  CustomMessage;

const gbdTimeoutText =
  'We’re unable to process the uploaded GDB file due to its complexity. Please simplify the file and try again.';

const suppressedMessages: SuppressMessagingItem[] = [
  // Success statuses - no toast needed for these
  { statusText: ResponseStatuses.Success },
  { statusText: ResponseStatuses.UserFound },
  { statusText: ResponseStatuses.SuccessNoResult },
  { statusText: ResponseStatuses.SuccessWithWarning },
  { statusText: ResponseStatuses.Warning },

  // Error statuses - api call succeeds but response contains an error status
  { statusText: ResponseStatuses.NoUserFound },
  { statusText: ResponseStatuses.DoesNotExist },
  { statusText: ResponseStatuses.ValidationFailed },

  {
    statusText: ResponseStatuses.AlreadyExist,
    url: '/help',
  },

  // GDB upload timeout on localhost - locally the server times out with a server response
  {
    statusText: ResponseStatuses.Failed,
    url: GeospatialEndpoints.getGeometryData(),
  },

  // Address checker service - error shown in UI
  {
    message: 'Failed to get USPS verified address',
    url: AddressValidationEndpoints.getAddress(),
  },
];

const suppressedErrorMessages: SuppressMessagingItem[] = [
  {
    url: IpacEndpoints.getSpecies(),
    customMessage: {
      console: 'The USFWS IPAC service is currently unavailable.',
    },
  },
  {
    url: 'ribits',
    customMessage: {
      console: 'RIBITS service is currently unavailable.',
    },
  },
  {
    url: ProfileEndpoints.getUserProfile(),
    customMessage: {
      console: 'Unable to retrieve user profile information.',
    },
  },
  {
    url: DeveloperAccessEndpoints.getBannerInfo(),
    customMessage: {
      console: 'Unable to retrieve banner information.',
    },
  },

  // GDB upload timeout - error is displayed inline by the file input
  {
    statusCodeRange: [502, 504],
    url: GeospatialEndpoints.getGeometryData(),
  },
  {
    statusCode: 413,
    url: GeospatialEndpoints.getGeometryData(),
  },

  // Some hosted environments return a network error without a status code
  {
    url: GeospatialEndpoints.getGeometryData(),
    message: 'Network Error',
    customMessage: {
      toast: gbdTimeoutText,
    },
  },

  {
    url: UserRoleCapabilityEndpoints.getUserRoleCapabilities(),
    customMessage: {
      toast: 'Unable to load user capabilities. Some actions may be unavailable.',
      console: 'User role capabilities endpoint failed. Falling back to default capabilities.',
    },
  },

  // These fail-over to backup endpoints so no error is needed
  { url: AorEndpoints.getStateEezDistrictBoundaries() },
  { url: AorEndpoints.getRegulatoryAorEezLayer() },
  { url: AorEndpoints.getRegulatoryAorLayer() },
];

export const displayMessages = ({
  defaultMessage,
  responseMessage,
  responseStatusCode,
  responseStatusText,
  responseUrl,
  type,
}: {
  defaultMessage?: string;
  responseMessage?: string;
  responseStatusCode?: number;
  responseStatusText?: (typeof ResponseStatuses)[keyof typeof ResponseStatuses];
  responseUrl?: string;
  type: 'success' | 'error';
}) => {
  const suppressedList = type === 'success' ? suppressedMessages : suppressedErrorMessages;

  const suppressedIndex = suppressedList.findIndex((item) => {
    const statusCode = Number(responseStatusCode);
    const statusCodeRange = item.statusCodeRange;

    const isStatusTextMatch = 'statusText' in item ? item.statusText === responseStatusText : true;
    const isStatusCodeMatch = 'statusCode' in item ? item.statusCode === statusCode : true;
    const isStatusCodeRangeMatch = statusCodeRange
      ? statusCode >= statusCodeRange[0] && statusCode <= statusCodeRange[1]
      : true;
    const isUrlMatch = 'url' in item ? responseUrl?.includes(item.url || '') : true;
    const isMessageMatch = 'message' in item ? responseMessage?.includes(item.message || '') : true;

    return isStatusTextMatch && isStatusCodeMatch && isStatusCodeRangeMatch && isUrlMatch && isMessageMatch;
  });
  const isSuppressed = suppressedIndex > -1;

  if (isSuppressed) {
    const customMessage = suppressedList[suppressedIndex]?.customMessage;

    if (customMessage?.toast) {
      toast.error(customMessage.toast, { autoClose: false });
    }
    if (customMessage?.console) {
      console.error(customMessage.console);
    }
  } else if (responseMessage) {
    toast.error(responseMessage || defaultMessage);
    console.error(responseMessage || defaultMessage);
  }
};
